import React, { useCallback, useEffect, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { useSnackBar } from '../../../../../../hooks/useSnackBar';
import Switch from '../../../../../forms/Switch';
import { addMacrosToTag, detectTagType } from './macroUtils';
import { getVendorName } from './tagTypes';

export default function GenerateMacros() {
 const { control, watch, setValue } = useFormContext();
 const { showSnackBar } = useSnackBar();
 const { field } = useController({ control, name: 'generateMacros' });
 
 const watchedTag = watch('autoTag');
 const originalTagRef = useRef('');
 const previousTagRef = useRef(watchedTag);
 const isMacroUpdateRef = useRef(false);

 useEffect(() => {
   // Skip if this HTML change was caused by macro update
   if (watchedTag !== previousTagRef.current && !isMacroUpdateRef.current) {
     if (!watchedTag) {
       field.onChange(false);
       originalTagRef.current = '';
     }
     previousTagRef.current = watchedTag;
   }
   // Reset the flag after effect runs
   isMacroUpdateRef.current = false;
 }, [watchedTag, field]);

 const handleToggleMacros = useCallback((checked: boolean) => {
   if (checked && watchedTag) {
     originalTagRef.current = watchedTag;
     const tagType = detectTagType(watchedTag);
     if (tagType) {
       try {
         // Set flag before updating HTML
         isMacroUpdateRef.current = true;
         const processedTag = addMacrosToTag(watchedTag, tagType);
         setValue('autoTag', processedTag, { shouldDirty: true });
         field.onChange(true);
         showSnackBar(`Applied macros for ${getVendorName(tagType)} tag`, 'success', {
           event: 'macros_generated',
           tag_type: tagType,
         });
       } catch (error) {
         const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
         field.onChange(false);
         showSnackBar(`Error processing tag: ${errorMessage}. Using original format.`, 'warning', {
           event: 'macros_generate_error',
           tag_type: tagType,
         });
       }
     }
   } else if (!checked && originalTagRef.current) {
     // Set flag before reverting HTML
     isMacroUpdateRef.current = true;
     setValue('autoTag', originalTagRef.current, { shouldDirty: true });
     originalTagRef.current = '';
     field.onChange(false);
   }
 }, [field, watchedTag, setValue, showSnackBar]);

 return (
   <Switch
     name="generateMacros"
     label="Generate Macros"
     checked={field.value}
     onChange={(e) => handleToggleMacros(e.target.checked)}
   />
 );
}